import { getChartType } from '@thoughtspot/chart-lib/lib/core/chart-model-utils';
import { create } from '@thoughtspot/logger';
import _ from 'lodash';
import {
    ChartConfigUpdateStatus,
    UNDEFINED_DISPLAYMODE,
} from '/@utils/answer-banner.util';
import {
    ApolloCache,
    ApolloClient,
    gql,
    Reference,
} from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import {
    Answer,
    AnswerEditSession,
    ChartViz,
} from '../generated/graphql-types';
import { getAnswerCacheKey, getCachedAnswer } from './answer-cache-service';

const logger = create('answer-viz-service');

/**
 *  Retrieves the current answer from the cache.
 */
export const getCurrentAnswerFromCache = (
    cache: ApolloCache<object>,
    answerSessionId: string,
): Partial<AnswerEditSession> => {
    const cachedAnswerSession = getCachedAnswer(
        cache,
        answerSessionId,
        gql`
            {
                id {
                    genNo
                }
                answer {
                    id
                    displayMode
                    visualizations {
                        ... on ChartViz {
                            id
                            config {
                                chartType
                                isLocked
                            }
                        }
                    }
                }
                previousTokenRefAnswer @client
            }
        `,
    );
    return {
        answer: cachedAnswerSession?.answer,
    };
};

/**
 * @param client
 * @param answerSessionId
 * Cache the current answer before new answer is updated to compare changes in the chart configuration with the new answer.
 */
export const setCachedAnswerBeforeMutation = (
    client: ApolloClient<object>,
    answerSessionId: string,
): void => {
    const { cache } = client;
    const { answer } = getCurrentAnswerFromCache(cache, answerSessionId);

    cache.writeFragment({
        id: getAnswerCacheKey(cache, answerSessionId),
        fragment: gql`
            fragment previousTokenRefAnswer on AnswerEditSession {
                previousTokenRefAnswer @client
            }
        `,
        data: {
            previousTokenRefAnswer: {
                answer,
                id: answerSessionId,
            },
        },
    });
};

/**
 * @param cache
 * @param answerSessionId
 * @param isUpdated
 *  Set the value of the ChartConfigUpdateStatus flag in the cache based on changes in the chart or display mode..
 */
export const setChartConfigUpdateStatus = (
    cache: ApolloCache<object>,
    answerSessionId: string,
    isUpdated: boolean,
): void => {
    // Fix for Apollo Client 3.13.5: Don't write to cache if answerSessionId is undefined
    if (!answerSessionId) {
        return;
    }

    try {
        cache.writeFragment({
            id: getAnswerCacheKey(cache, answerSessionId),
            fragment: gql`
                fragment chartConfigUpdateStatus on AnswerEditSession {
                    chartConfigUpdateStatus @client
                }
            `,
            data: {
                chartConfigUpdateStatus: {
                    id: answerSessionId,
                    isUpdated,
                },
            },
        });
    } catch (error) {
        if (error?.message?.includes('already recomputing')) {
            // Suppress "already recomputing" error to prevent crashes
            return;
        }
        // Re-throw any other errors
        throw error;
    }
};

/**
 * @param cache
 * @param answerSessionId
 *  The function will read the cached chartConfigUpdateStatus value, and based on this value,
 *  it will determine whether to show an information banner in the answer content container.
 * if we dont have value in cache it will return null.
 */
export const readChartConfigUpdateStatus = (
    cache: ApolloCache<object>,
    answerSessionId: string,
): ChartConfigUpdateStatus | null => {
    const answerEditSession: any = getCachedAnswer(
        cache,
        answerSessionId,
        gql`
            {
                chartConfigUpdateStatus @client
            }
        `,
    );
    return answerEditSession?.chartConfigUpdateStatus ?? null;
};

/**
 * @param cache
 * @param answerSessionId
 * Retrieves a previous reference answer object from the cache.
 */
export const getPreviousRefAnswerFromCache = (
    cache: ApolloCache<object>,
    answerSessionId: string,
): Answer => {
    const answerEditSession: any = getCachedAnswer(
        cache,
        answerSessionId,
        gql`
            {
                previousTokenRefAnswer @client
            }
        `,
    );
    return answerEditSession?.previousTokenRefAnswer?.answer ?? null;
};

/**
 * @param cache
 * @param cacheAnswer
 * @param hasPreviousCachedAnswer
 *  extracting a cached chart visualization object from a cache answer object.
 */
export const extractCachedChartVizFromAnswer = (
    props: any,
    cacheAnswer: Answer,
    hasPreviousCachedAnswer = false,
): ChartViz => {
    const { cache } = props;
    // Extract the array of visualizations from the cached answer object,
    // mapping over any references and replacing them with the corresponding objects.
    let chartVizObj: any = {};
    const vizs =
        (hasPreviousCachedAnswer
            ? cacheAnswer?.visualizations
            : cacheAnswer?.visualizations?.map(
                  // eslint-disable-next-line no-underscore-dangle
                  (ref: any) => cache.extract()[ref.__ref],
              ).filter(Boolean)) || [];
    chartVizObj = vizs.find((viz: any) => {
        /* eslint-disable-next-line no-underscore-dangle */
        return viz.__typename === 'ChartViz';
    });
    // Return the chart viz object if found, otherwise null.
    return chartVizObj ?? null;
};

/**
 * @param previousCachedAnswer
 * @param updatedAnswer
 * @param props {readField,cache}
 * @param answerDisplayMode
 * @param answerSessionId
 * We compare the chart type and display mode in the new answer and previousAnswer.
 * If either of them has changed, we update the ChartConfigUpdateStatus to true.
 * This updated value is then read in the answer content container,
 * which displays the chart configuration invalid info bar.
 */
export const isChartOrDisplayModeChanged = (
    previousAnswer: ChartViz,
    updatedAnswer: ChartViz,
    answerDisplayModeConfig: any,
    props: any,
    answerSessionId: string,
): void => {
    const { cache } = props;

    const isDisplayModeChanged =
        answerDisplayModeConfig?.previousDisplaymode !==
        answerDisplayModeConfig?.updatedDisplaymode;
    const isChartTypeChanged =
        getChartType(previousAnswer) !== getChartType(updatedAnswer);

    if (
        previousAnswer.config?.isLocked &&
        (isDisplayModeChanged || isChartTypeChanged)
    ) {
        setChartConfigUpdateStatus(cache, answerSessionId, true);
    }
};
/**
 * @param previousAnswer
 * @param updatedAnswer
 * @param hasPreviousCachedAnswer
 * @returns displayMode
 * For new answers, displayMode value we are getting as 'UNDEFINED' in this case we are taking the suggestedDisplayMode value as the display mode.
 */
export const getDisplayModes = (
    previousAnswer: Answer,
    updatedAnswer: Answer,
    hasPreviousCachedAnswer = false,
): string => {
    if (hasPreviousCachedAnswer) {
        return previousAnswer.displayMode;
    }
    // For saved answer we are getting displayMode value instead of UNDEFINED we need to consider this case also.
    return updatedAnswer.displayMode !== UNDEFINED_DISPLAYMODE
        ? updatedAnswer.displayMode
        : updatedAnswer.suggestedDisplayMode;
};

/**
 * @param existing
 * @param incoming
 * @param props {readField,cache}
 * In this function, we read the previous answer from the getPreviousRefAnswerFromCache method, which returns the previous answer from the cache. We then extract the updated answer from it.
 * Finally, we send both the previous and updated answers to check if the chart or display mode has changed.
 */
export const validateChartAndDisplayModeForNewQuery = (
    existing: Reference,
    incoming: Reference,
    props: any,
): void => {
    const { readField, cache } = props;
    const answerSessionId: string = readField(
        'sessionId',
        readField('id', existing),
    );
    let previousCachedAnswer: any = null;
    let updatedAnswer: any = null;
    let answerDisplayMode: any = {};
    // Privious cached answer
    const previousRefAnswer = getPreviousRefAnswerFromCache(
        cache,
        answerSessionId,
    );

    // New search we don't have reference in  cache and if we enter invlaid sage query previousDisplaymode value getting UNDEFINED no need to validate in this case.
    if (
        _.isNil(previousRefAnswer) ||
        previousRefAnswer?.displayMode === UNDEFINED_DISPLAYMODE ||
        previousRefAnswer?.visualizations?.length === 0
    ) {
        // If banner visible and we click on close icon in answer agian new search we need to hide banner.
        setChartConfigUpdateStatus(cache, answerSessionId, false);
        return;
    }

    // Updated answer
    // eslint-disable-next-line no-underscore-dangle
    const incomingAnswer = cache.extract()[readField('answer', incoming).__ref];
    answerDisplayMode = {
        previousDisplaymode: getDisplayModes(
            previousRefAnswer,
            incomingAnswer,
            true,
        ),
        updatedDisplaymode: getDisplayModes(
            incomingAnswer,
            incomingAnswer,
            false,
        ),
    };
    previousCachedAnswer = extractCachedChartVizFromAnswer(
        props,
        previousRefAnswer,
        true,
    );

    if (!previousCachedAnswer) return;

    updatedAnswer = extractCachedChartVizFromAnswer(
        props,
        incomingAnswer,
        false,
    );
    isChartOrDisplayModeChanged(
        previousCachedAnswer,
        updatedAnswer,
        answerDisplayMode,
        props,
        answerSessionId,
    );
};
